<template>
	<div class="lists-page">
		<div class="lists-layout">
			<!-- Left Panel: List Selection & Items -->
			<div class="lists-list-panel">
				<div class="list-header">
					<h2>Lists</h2>
				</div>

				<!-- List Type Selector -->
				<div class="list-selector">
					<select v-model="selectedListType" class="form-input" @change="onListTypeChanged">
						<option :value="null" disabled>Select a list...</option>
						<option v-for="opt in listOptions" :key="opt.route" :value="opt">
							{{ opt.displayName }}
						</option>
					</select>
				</div>

				<!-- Search & Count -->
				<div v-if="selectedListType && list" class="list-meta">
					<span class="item-count">{{ list.length }} {{ selectedListType.displayName }}</span>
					<button v-if="!selectedListType.editOnly" class="action-btn primary small" @click="newItemPressed">+ New</button>
				</div>

				<div v-if="selectedListType" class="search-box">
					<input
						v-model="search"
						type="text"
						placeholder="Search items..."
						class="search-input"
					/>
				</div>

				<!-- Items List -->
				<div class="items-list">
					<div v-if="filteredList && filteredList.length > 0" class="item-items">
						<div
							v-for="item in filteredList"
							:key="item.id || item.name"
							class="item-row"
							:class="{ active: selectedItem && selectedItem === item }"
							@click="selectItem(item)"
						>
							{{ item.name }}
						</div>
					</div>
					<div v-else-if="selectedListType && list" class="empty-state">
						No items found
					</div>
					<div v-else-if="!selectedListType" class="empty-state welcome">
						Select a list to get started
					</div>
				</div>
			</div>

			<!-- Right Panel: Item Details -->
			<div class="item-details-panel">
				<div v-if="selectedItem" class="details-content">
					<!-- Item ID -->
					<div v-if="selectedItem.id" class="detail-header">
						{{ selectedItem.id }}
					</div>
					<div v-else class="detail-header new-item">
						New Item
					</div>

					<!-- Top Actions -->
					<div class="top-actions">
						<button class="action-btn primary" @click="saveItem">
							{{ selectedItem.id ? 'Save Changes' : 'Create Item' }}
						</button>
						<button v-if="selectedItem.id && !selectedListType.editOnly" class="action-btn danger" @click="showDeleteConfirm = true">Delete</button>
						<button v-if="!selectedItem.id" class="action-btn secondary" @click="cancelNewItem">Cancel</button>
					</div>

					<!-- Dynamic Form Fields -->
					<div class="form-fields">
						<div v-for="(value, key) in selectedItem" :key="key" class="form-group">
							<template v-if="key !== 'id'">
								<label>{{ formatLabel(key) }}</label>
								<div v-if="isBoolean(selectedItem[key])" class="checkbox-field">
									<input v-model="selectedItem[key]" type="checkbox" class="form-checkbox" />
								</div>
								<div v-else-if="isImageField(key)" class="image-field">
									<input v-model="selectedItem[key]" type="text" class="form-input" />
									<img
										v-if="selectedItem[key]"
										:src="selectedItem[key]"
										class="field-image-preview"
										@error="$event.target.style.display = 'none'"
									/>
								</div>
								<input v-else v-model="selectedItem[key]" type="text" class="form-input" />
							</template>
						</div>
					</div>
				</div>
				<div v-else class="empty-state">
					{{ selectedListType ? 'Select an item to view details' : 'Select a list to get started' }}
				</div>
			</div>
		</div>

		<!-- Delete Confirmation -->
		<div v-if="showDeleteConfirm" class="modal-overlay" @click="showDeleteConfirm = false">
			<div class="modal-dialog" @click.stop>
				<div class="modal-header">
					<h3>Delete Item</h3>
					<button class="close-btn" @click="showDeleteConfirm = false">&#10005;</button>
				</div>
				<div class="modal-body">
					<p>Are you sure you want to delete <strong>{{ selectedItem.name }}</strong>?</p>
				</div>
				<div class="modal-footer">
					<button class="action-btn danger" @click="deleteItem">Delete</button>
					<button class="action-btn secondary" @click="showDeleteConfirm = false">Cancel</button>
				</div>
			</div>
		</div>

		<!-- Success Toast -->
		<v-snackbar v-model="showSuccessToast" :timeout="3000" location="bottom" color="success">
			{{ successMessage }}
			<template #actions>
				<v-btn icon="mdi-close" size="small" @click="showSuccessToast = false"></v-btn>
			</template>
		</v-snackbar>

		<!-- Error Toast -->
		<v-snackbar v-model="showErrorToast" :timeout="4000" location="bottom" color="error">
			{{ errorMessage }}
			<template #actions>
				<v-btn icon="mdi-close" size="small" @click="showErrorToast = false"></v-btn>
			</template>
		</v-snackbar>
	</div>
</template>

<script>
import AdminServices from '@/admin/mixins/AdminServices';

export default {
	name: 'ListsPage',
	mixins: [AdminServices],
	data() {
		return {
			selectedListType: null,
			list: null,
			selectedItem: null,
			search: '',
			showDeleteConfirm: false,
			showSuccessToast: false,
			successMessage: '',
			showErrorToast: false,
			errorMessage: '',
			listOptions: [
				{ route: 'InsuranceProviders', displayName: 'Insurance Providers', editOnly: false },
				{ route: 'InvestmentProviders', displayName: 'Investment Providers', editOnly: false },
				{ route: 'CreditProviders', displayName: 'Credit Providers', editOnly: false },
				{ route: 'InvestmentTypes', displayName: 'Investment Types', editOnly: false },
				{ route: 'CreditTypes', displayName: 'Credit Types', editOnly: false },
				{ route: 'PolicyTypes', displayName: 'Insurance Policy Types', editOnly: false },
				{ route: 'Banks', displayName: 'Banks', editOnly: false },
				{ route: 'BankAccountTypes', displayName: 'Bank Account Types', editOnly: true }
			]
		};
	},
	computed: {
		filteredList() {
			if (!this.list) return null;
			if (!this.search || !this.search.trim()) return this.list;
			const query = this.search.toLowerCase();
			return this.list.filter(item => item.name && item.name.toLowerCase().includes(query));
		}
	},
	methods: {
		formatLabel(key) {
			return key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');
		},
		isBoolean(value) {
			return value === true || value === false;
		},
		isImageField(key) {
			const lower = key.toLowerCase();
			return lower.includes('logo') || lower.includes('image') || lower.includes('icon');
		},
		showToast(message) {
			this.successMessage = message;
			this.showSuccessToast = true;
		},
		showError(message) {
			this.errorMessage = message || "Sorry, that didn't work. Please contact tech.";
			this.showErrorToast = true;
		},
		onListTypeChanged() {
			this.selectedItem = null;
			this.search = '';
			this.loadList(true);
		},
		loadList(autoSelect) {
			if (!this.selectedListType) return;
			this._GET('/' + this.selectedListType.route, (result) => {
				this.list = result;
				if (autoSelect && this.list.length > 0) {
					this.selectItem(this.list[0]);
				}
			});
		},
		selectItem(item) {
			this.selectedItem = item;
		},
		newItemPressed() {
			const newItem = {};
			if (this.list && this.list.length > 0) {
				for (const key in this.list[0]) {
					if (key.toLowerCase() !== 'id') {
						const sample = this.list[0][key];
						if (sample === true || sample === false) {
							newItem[key] = false;
						} else {
							newItem[key] = '';
						}
					}
				}
				newItem.name = 'New Item';
				newItem.id = '';
			}
			this.list.splice(0, 0, newItem);
			this.selectItem(newItem);
		},
		cancelNewItem() {
			this.list.splice(0, 1);
			if (this.list.length > 0) {
				this.selectItem(this.list[0]);
			} else {
				this.selectedItem = null;
			}
		},
		saveItem() {
			if (!this.selectedItem || !this.selectedListType) return;
			const route = this.selectedListType.route;

			if (this.selectedItem.id === '') {
				const data = { ...this.selectedItem };
				delete data.id;
				this.$http.post(this.api + '/' + route, data)
					.then((result) => {
						this.selectItem(result.data);
						this.loadList(false);
						this.showToast('Item created successfully');
					})
					.catch(() => { this.showError(); });
			} else {
				this.$http.put(this.api + '/' + route + '/' + this.selectedItem.id, this.selectedItem)
					.then((result) => {
						this.selectItem(result.data);
						this.loadList(false);
						this.showToast('Item saved successfully');
					})
					.catch(() => { this.showError(); });
			}
		},
		deleteItem() {
			if (!this.selectedItem || !this.selectedListType) return;
			this._DELETE('/' + this.selectedListType.route + '/' + this.selectedItem.id, () => {
				this.showDeleteConfirm = false;
				this.selectedItem = null;
				this.loadList(true);
				this.showToast('Item deleted successfully');
			});
		}
	}
};
</script>

<style scoped>
.lists-page {
	min-height: 100%;
	padding: 20px;
}

.lists-layout {
	display: grid;
	grid-template-columns: 300px 1fr;
	gap: 20px;
	height: calc(100vh - 40px);
}

/* Left Panel */
.lists-list-panel {
	background-color: var(--admin-bg-secondary);
	border-radius: var(--border-radius-m);
	padding: 20px;
	display: flex;
	flex-direction: column;
	box-shadow: var(--box-shadow);
	overflow-y: auto;
}

.list-header {
	margin-bottom: 15px;
}

.list-header h2 {
	margin: 0;
	font-size: 16px;
	color: var(--admin-text-primary);
}

.list-selector {
	margin-bottom: 15px;
}

.list-selector select {
	width: 100%;
}

.list-meta {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 10px;
}

.item-count {
	font-size: 12px;
	color: var(--admin-text-secondary);
}

.action-btn.small {
	padding: 6px 12px;
	font-size: 12px;
}

.items-list {
	flex: 1;
	overflow-y: auto;
}

.item-items {
	display: flex;
	flex-direction: column;
	gap: 2px;
}

.item-row {
	padding: 12px;
	background-color: var(--admin-bg-secondary);
	color: var(--admin-text-primary);
	border-radius: var(--border-radius-s);
	font-size: 13px;
	transition: all 0.2s ease;
	border-left: 3px solid transparent;
}

.item-row:hover {
	background-color: var(--admin-hover-bg);
}

.item-row.active {
	background-color: var(--admin-active-bg);
	border-left-color: var(--background-color-primary);
}

/* Right Panel */
.item-details-panel {
	background-color: var(--admin-bg-secondary);
	border-radius: var(--border-radius-m);
	padding: 30px;
	overflow-y: auto;
	box-shadow: var(--box-shadow);
}

.details-content {
	max-width: 600px;
}

.detail-header {
	font-size: 13px;
	color: var(--admin-text-secondary);
	margin-bottom: 20px;
	padding: 10px;
	background-color: var(--admin-hover-bg);
	border-radius: var(--border-radius-s);
	font-family: monospace;
}

.detail-header.new-item {
	color: var(--background-color-primary);
}

/* Top Actions */
.top-actions {
	display: flex;
	gap: 10px;
	margin-bottom: 30px;
	padding-bottom: 20px;
	border-bottom: 1px solid var(--admin-border);
}

/* Form Fields */
.form-fields {
	display: flex;
	flex-direction: column;
	gap: 18px;
}

.form-fields .form-group {
	margin-bottom: 0;
}

.checkbox-field {
	padding-top: 4px;
}

.image-field {
	display: flex;
	align-items: center;
	gap: 12px;
}

.image-field .form-input {
	flex: 1;
}

.field-image-preview {
	width: 40px;
	height: 40px;
	border-radius: var(--border-radius-s);
	object-fit: contain;
	background-color: var(--admin-hover-bg);
	border: 1px solid var(--admin-border);
	flex-shrink: 0;
}

/* Welcome state */
.empty-state.welcome {
	padding-top: 60px;
	font-size: 14px;
}

/* Snackbar positioning */
:deep(.v-snackbar) {
	bottom: 100px !important;
	top: auto !important;
	margin-top: 0 !important;
}

/* Responsive */
@media (max-width: 1024px) {
	.lists-layout {
		grid-template-columns: 250px 1fr;
	}
}

@media (max-width: 480px) {
	.lists-layout {
		grid-template-columns: 1fr;
		gap: 15px;
	}

	.lists-list-panel {
		max-height: 400px;
	}

	.lists-page {
		padding: 10px;
	}
}
</style>
